/**
 * S08 — THE LIFESTYLE ECOSYSTEM
 *
 * Brand Universe & Merchandise Strategy (Pg8). Text-heavy editorial
 * page with the thesis, two groups (Product Line + Activations),
 * and a closing line. Simple atmospheric background.
 */

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy, brandUniverse } from "@/lib/voice";
import { keyImages, moodboardUrl } from "@/lib/moodboard";

gsap.registerPlugin(ScrollTrigger);

export function S08_TheCrew() {
  const copy = sectionCopy.s08;
  const sectionRef = useRef<HTMLElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      itemRefs.current.forEach((r) => {
        if (!r) return;
        gsap.fromTo(
          r,
          { opacity: 0, x: -20 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: r,
              start: "top 90%",
              end: "top 65%",
              scrub: 1,
            },
          },
        );
      });
    }, el);
    return () => ctx.revert();
  }, []);

  let itemIdx = 0;

  return (
    <section
      ref={sectionRef}
      id="s08"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        flexDirection: "column",
        padding: "14vh var(--space-lg) 14vh",
        minHeight: "auto",
      }}
    >
      {/* Atmospheric background — Truffe Noire specimen, very dim */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${moodboardUrl(keyImages.truffeNoire)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%) contrast(1.4) brightness(0.22)",
          mixBlendMode: "luminosity",
          opacity: 0.55,
          zIndex: 0,
        }}
      />
      {/* Vignette */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.75) 50%, rgba(5,5,5,0.95) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Header */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          textAlign: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <div
          className="type-meta-sm"
          style={{
            color: "var(--flame)",
            marginBottom: "var(--space-md)",
            letterSpacing: "var(--ls-meta-wide)",
          }}
        >
          {copy.meta}
        </div>
        <h2
          className="type-hero-lg"
          style={{
            color: "var(--bone)",
            margin: 0,
            textShadow: "0 4px 40px rgba(0,0,0,0.95)",
          }}
        >
          {copy.hero}
        </h2>
        {copy.subhero && (
          <p
            className="type-voice"
            style={{
              color: "var(--fog)",
              marginTop: "var(--space-md)",
              fontSize: "clamp(1.125rem, 1.4vw, 1.5rem)",
              fontStyle: "italic",
            }}
          >
            {copy.subhero}
          </p>
        )}
      </div>

      {/* Thesis paragraph */}
      <p
        style={{
          position: "relative",
          zIndex: 3,
          fontFamily: "var(--f-serif-italic)",
          fontStyle: "italic",
          fontSize: "clamp(1.0625rem, 1.3vw, 1.375rem)",
          lineHeight: 1.55,
          color: "var(--bone)",
          opacity: 0.92,
          maxWidth: "70ch",
          margin: "0 auto var(--space-2xl)",
          textAlign: "center",
          textShadow: "0 2px 20px rgba(0,0,0,0.95)",
        }}
      >
        {brandUniverse.thesis}
      </p>

      {/* Two groups: Product Line + Activations */}
      <div
        className="s08-groups"
        style={{
          position: "relative",
          zIndex: 3,
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "var(--space-xl)",
          width: "min(1400px, 92vw)",
          margin: "0 auto",
          marginBottom: "var(--space-2xl)",
        }}
      >
        {brandUniverse.groups.map((group) => (
          <div key={group.label}>
            {/* Group label */}
            <div
              className="type-meta-sm"
              style={{
                color: "var(--flame)",
                letterSpacing: "var(--ls-meta-wide)",
                marginBottom: "var(--space-xs)",
              }}
            >
              {group.label}
            </div>
            <div
              className="type-meta-lg"
              style={{
                color: "var(--bone)",
                opacity: 0.6,
                letterSpacing: "var(--ls-meta-wide)",
                marginBottom: "var(--space-md)",
                paddingBottom: "var(--space-md)",
                borderBottom: "1px solid rgba(232, 106, 29, 0.4)",
              }}
            >
              {group.sub}
            </div>

            {/* Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}>
              {group.items.map((item) => {
                const currentIdx = itemIdx++;
                return (
                  <div
                    key={item.name}
                    ref={(el) => {
                      itemRefs.current[currentIdx] = el;
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--f-serif-display)",
                        fontWeight: 900,
                        fontStretch: "75%",
                        fontSize: "clamp(1.125rem, 1.4vw, 1.5rem)",
                        color: "var(--bone)",
                        textTransform: "uppercase",
                        letterSpacing: "0.01em",
                        marginBottom: "var(--space-2xs)",
                      }}
                    >
                      {item.name}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--f-serif-body)",
                        fontSize: "clamp(0.875rem, 1vw, 1rem)",
                        lineHeight: 1.55,
                        color: "var(--bone)",
                        opacity: 0.8,
                      }}
                    >
                      {item.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Close */}
      <p
        style={{
          position: "relative",
          zIndex: 3,
          fontFamily: "var(--f-serif-italic)",
          fontStyle: "italic",
          fontSize: "clamp(1rem, 1.2vw, 1.25rem)",
          lineHeight: 1.55,
          color: "var(--flame)",
          opacity: 0.92,
          maxWidth: "68ch",
          margin: "0 auto",
          textAlign: "center",
          textShadow: "0 2px 16px rgba(0,0,0,0.9)",
        }}
      >
        {brandUniverse.close}
      </p>
    </section>
  );
}
